import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import { CMWorkflowClient } from './CMWorkflowClient';
import { GlowpickClient } from './GlowpickClient';

export interface CpGpMatchRequest {
  cpProductId: string;
  cpItemId: string;
  gpProductId: string;
  keyword: string;
  thumbnailImageUrl?: string;
  productPageUrl?: string;
}

export interface CpGpMatchResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export class CpGpMatchClient {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.SOURCING);
    this.apiKey = getConfig().apiKey;
  }

  // Create CP-GP match
  async createMatch(matchData: CpGpMatchRequest): Promise<CpGpMatchResponse> {
    const url = `${this.baseUrl}/sourcing/cpGpMatched`;

    try {
      const response = await axios.post(url, matchData, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      console.info('✅ CP-GP match created successfully');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Failed to create CP-GP match:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Update CP-GP match
  async updateMatch(cpProductId: string, cpItemId: string, updateData: Partial<any>): Promise<CpGpMatchResponse> {
    const url = `${this.baseUrl}/sourcing/cpGpMatched/${cpProductId}/${cpItemId}`;
    console.info('🔄 Updating CP-GP match:', url, updateData);

    try {
      const response = await axios.patch(url, updateData, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      console.info('✅ CP-GP match updated successfully');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Failed to update CP-GP match:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Delete CP-GP match
  async deleteMatch(cpProductId: string, cpItemId: string): Promise<CpGpMatchResponse> {
    try {
      const matchResult = await this.getMatchByCpProductAndItemId(cpProductId, cpItemId);
      const gpProductId = matchResult.match?.gpProductId;

      if (gpProductId) {
        // CatalogManagement is keyed by (gpi, cppi) where cppi is the pure Coupang Product ID
        const cmWorkflowClient = new CMWorkflowClient();
        try {
          await cmWorkflowClient.deleteCMWorkflow(gpProductId, cpProductId);
          console.info('✅ CM workflow deleted');
        } catch (cmError) {
          console.warn('⚠️ Failed to delete CM workflow (non-critical):', cmError);
        }

        // Glowpick metadata.cpProducts is keyed by cpUniqueId in `${cpProductId}-${cpItemId}` form
        const glowpickClient = new GlowpickClient();
        const cpUniqueId = `${cpProductId}-${cpItemId}`;
        try {
          await glowpickClient.deleteCpProductsFromGlowpickMetadata(gpProductId, cpUniqueId);
        } catch (metadataError) {
          console.warn('⚠️ Failed to update Glowpick metadata (non-critical):', metadataError);
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch match details for cleanup (non-critical):', error);
    }

    const url = `${this.baseUrl}/sourcing/cpGpMatched/${cpProductId}/${cpItemId}`;
    console.info('🗑️ Deleting CP-GP match:', url);

    try {
      const response = await axios.delete(url, {
        headers: { 'X-API-Key': this.apiKey },
      });

      console.info('✅ CP-GP match deleted successfully');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Failed to delete CP-GP match:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Get existing matches for a GP product
  async getMatchesForGpProduct(gpProductId: string): Promise<{ matches: any[] }> {
    const url = `${this.baseUrl}/sourcing/cpGpMatched?gpProductId=${gpProductId}`;
    console.info('🔍 Getting CP-GP matches for GP product:', url);

    try {
      const response = await axios.get(url, {
        headers: { 'X-API-Key': this.apiKey },
      });

      console.info(`✅ Found ${response.data.products?.length || 0} matches for GP product ${gpProductId}`);
      console.info('Response data:', response.data);
      return {
        matches: response.data.products || [],
      };
    } catch (error) {
      console.error('❌ Failed to get CP-GP matches:', error);
      return { matches: [] };
    }
  }

  // Get single matched product by cpProductId (kept for potential future use)
  async getMatchByCpProductId(cpProductId: string): Promise<{ match?: any; success: boolean; message?: string }> {
    const url = `${this.baseUrl}/sourcing/cpGpMatched/${cpProductId}`;
    console.info('🔍 Getting CP-GP match by product ID:', url);

    try {
      const response = await axios.get(url, {
        headers: { 'X-API-Key': this.apiKey },
      });

      console.info(`✅ Found match for CP product ${cpProductId}`);
      return {
        success: true,
        match: response.data.product || response.data || null,
      };
    } catch (error) {
      console.error('❌ Failed to get CP-GP match:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Get single matched product by both cpProductId and cpItemId
  async getMatchByCpProductAndItemId(
    cpProductId: string,
    cpItemId: string,
  ): Promise<{ match?: any; success: boolean; message?: string }> {
    const url = `${this.baseUrl}/sourcing/cpGpMatched/${cpProductId}/${cpItemId}`;
    console.info('🔍 Getting CP-GP match by product and item ID:', url);

    try {
      const response = await axios.get(url, {
        headers: { 'X-API-Key': this.apiKey },
      });

      console.info(`✅ Found match for CP product ${cpProductId}/${cpItemId}`);
      return {
        success: true,
        match: response.data.product || response.data || null,
      };
    } catch (error) {
      console.error('❌ Failed to get CP-GP match:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Get all matched products with pagination
  async getAllMatches(
    limit = 100,
    lastEvaluatedKey?: string,
    gpProductIds?: string[],

    cpProductId?: string,
    cpItemId?: string,
  ): Promise<{ matches: any[]; hasNextPage?: boolean; lastEvaluatedKey?: string }> {
    let url = `${this.baseUrl}/sourcing/cpGpMatched?limit=${limit}`;
    if (gpProductIds && gpProductIds.length > 0) {
      url += `&gpProductIds=${gpProductIds.join(',')}`;
    }

    if (cpProductId) {
      url += `&cpProductId=${cpProductId}`;
    }
    if (cpItemId) {
      url += `&cpItemId=${cpItemId}`;
    }
    if (lastEvaluatedKey) {
      url += `&lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`;
    }

    console.info('🔍 Getting all CP-GP matches:', url);

    try {
      const response = await axios.get(url, {
        headers: { 'X-API-Key': this.apiKey },
      });

      console.info(`✅ Found ${response.data.products?.length || 0} matched products`);
      return {
        matches: response.data.products || [],
        hasNextPage: Boolean(response.data.lastEvaluatedKey),
        lastEvaluatedKey: response.data.lastEvaluatedKey,
      };
    } catch (error) {
      console.error('❌ Failed to get all CP-GP matches:', error);
      return { matches: [] };
    }
  }

  // Delete all matches for a GP product
  async deleteAllMatchesForGpProduct(
    gpProductId: string,
  ): Promise<{ success: boolean; deletedCount?: number; message?: string }> {
    try {
      console.info(`🗑️ Deleting all matches for GP product: ${gpProductId}`);

      // First, get all existing matches for this GP product
      const result = await this.getMatchesForGpProduct(gpProductId);

      if (result.matches && result.matches.length > 0) {
        console.info(`Found ${result.matches.length} existing matches to delete`);

        let deletedCount = 0;
        const errors: string[] = [];

        // Delete each match
        for (const match of result.matches) {
          try {
            const deleteResult = await this.deleteMatch(match.cpProductId, match.cpItemId);
            if (deleteResult.success) {
              deletedCount++;
            } else {
              errors.push(`Failed to delete match ${match.cpProductId}-${match.cpItemId}: ${deleteResult.message}`);
            }
          } catch (error) {
            errors.push(
              `Error deleting match ${match.cpProductId}-${match.cpItemId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
          }
        }

        if (errors.length > 0) {
          console.error(`❌ Some matches failed to delete:`, errors);
          return {
            success: false,
            deletedCount,
            message: `Deleted ${deletedCount}/${result.matches.length} matches. Errors: ${errors.join(', ')}`,
          };
        }

        console.info(`✅ Successfully deleted all ${deletedCount} matches for GP product ${gpProductId}`);
        return { success: true, deletedCount };
      } else {
        console.info('No existing matches found to delete');
        return { success: true, deletedCount: 0 };
      }
    } catch (error) {
      console.error(`❌ Error deleting matches for GP product ${gpProductId}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const cpGpMatchClient = new CpGpMatchClient();
